import { CalendarEvent, CalendarMonthGroup, CalendarValue, HalfYear } from './calendarTypes';

const DETAIL_LABELS: Record<string, string> = {
    beschreibung: 'Beschreibung',
    description: 'Beschreibung',
    details: 'Details',
    time: 'Beginn',
    'end-time': 'Ende',
    endTime: 'Ende',
    date: 'Datum',
    'end-date': 'Enddatum',
    endDate: 'Enddatum',
    start: 'Beginn',
    end: 'Ende',
    hinweis: 'Hinweis',
    ort: 'Ort',
    location: 'Ort',
    treffpunkt: 'Treffpunkt',
    teilnehmer: 'Teilnehmer',
    agenda: 'Agenda',
    material: 'Material',
    themen: 'Themen',
    notiz: 'Notiz',
};

export function parseLocalDate(date: string): Date {
    const [year, month, day] = date.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
}

export function getTodayKey(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function getMonthKey(date: string): string {
    return date.slice(0, 7);
}

export function getMonthLabel(monthKey: string): string {
    const date = parseLocalDate(`${monthKey}-01`);

    return new Intl.DateTimeFormat('de-DE', {
        month: 'long',
        year: 'numeric',
    }).format(date);
}

export function formatDate(date: string): string {
    return new Intl.DateTimeFormat('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(parseLocalDate(date));
}

export function stringifyCalendarValue(value: CalendarValue | undefined): string {
    if (value === undefined || value === null || value === '') {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => stringifyCalendarValue(item))
            .filter(Boolean)
            .join(', ');
    }

    return Object.entries(value)
        .map(([key, item]) => `${formatDetailLabel(key)}: ${stringifyCalendarValue(item)}`)
        .filter(Boolean)
        .join('; ');
}

export function getEventDescription(event: CalendarEvent): string {
    return event.beschreibung ?? event.description ?? event.details ?? '';
}

export function getEventLocation(event: CalendarEvent): string {
    return event.ort ?? event.location ?? '';
}

export function getEventEndTime(event: CalendarEvent): string {
    return event['end-time'] ?? event.endTime ?? '';
}

export function getEventTimeLabel(event: CalendarEvent): string {
    const start = event.time ?? '';
    const end = getEventEndTime(event);

    if (start && end) {
        return `${start} bis ${end}`;
    }

    return start || end;
}

export function getEventDateLine(event: CalendarEvent): string {
    const parts = [formatDate(event.date), getEventTimeLabel(event), getEventLocation(event)].filter(Boolean);
    return parts.join(' · ');
}

export function isPastEvent(event: CalendarEvent, todayKey = getTodayKey()): boolean {
    return event.date.slice(0, 10) < todayKey;
}

export function isCurrentMonthEvent(event: CalendarEvent, todayKey = getTodayKey()): boolean {
    return getMonthKey(event.date) === getMonthKey(todayKey);
}

export function shouldShowEvent(
    event: CalendarEvent,
    showPastCurrentMonth: boolean,
    showPastMonths: boolean,
    todayKey = getTodayKey(),
): boolean {
    if (!isPastEvent(event, todayKey)) {
        return true;
    }

    if (isCurrentMonthEvent(event, todayKey)) {
        return showPastCurrentMonth;
    }

    return showPastMonths;
}

export function groupEventsByMonth(events: CalendarEvent[]): CalendarMonthGroup[] {
    const groups = new Map<string, CalendarEvent[]>();

    for (const event of [...events].sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;

        return (a.time ?? '').localeCompare(b.time ?? '') || a.title.localeCompare(b.title, 'de');
    })) {
        const key = getMonthKey(event.date);
        groups.set(key, [...(groups.get(key) ?? []), event]);
    }

    return Array.from(groups.entries()).map(([key, groupedEvents]) => ({
        key,
        label: getMonthLabel(key),
        events: groupedEvents,
    }));
}

export function formatDetailLabel(key: string): string {
    if (DETAIL_LABELS[key]) {
        return DETAIL_LABELS[key];
    }

    return key
        .replaceAll('-', ' ')
        .replaceAll('_', ' ')
        .replace(/^./, (char) => char.toUpperCase());
}

export function getOptionalDetailEntries(event: CalendarEvent): Array<[string, CalendarValue]> {
    const hidden = new Set([
        'id',
        'title',
        'name',
        'date',
        'time',
        'end-time',
        'endTime',
        'start',
        'end',
        'end-date',
        'endDate',
        'beschreibung',
        'description',
        'details',
        'ort',
        'location',
        'color',
        'farbe',
        'type',
        'active',
        '_permissions',
        '_applied_layers',
    ]);

    const preferred = ['treffpunkt', 'hinweis', 'notiz', 'agenda', 'themen', 'material', 'teilnehmer'];

    return Object.entries(event)
        .filter(([key, value]) => !hidden.has(key) && value !== undefined && value !== null && value !== '')
        .sort(([a], [b]) => {
            const indexA = preferred.indexOf(a);
            const indexB = preferred.indexOf(b);

            if (indexA === -1 && indexB === -1) return a.localeCompare(b, 'de');
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        }) as Array<[string, CalendarValue]>;
}

export function getCurrentHalfYear(today = new Date()): HalfYear {
    return {
        year: today.getFullYear(),
        half: today.getMonth() < 6 ? 1 : 2,
    };
}

export function getHalfYearMonths(halfYear: HalfYear): number[] {
    const start = halfYear.half === 1 ? 1 : 7;
    return Array.from({ length: 6 }, (_, index) => start + index);
}

export function getDaysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

export function getWeekdayLabel(year: number, month: number, day: number): string {
    return new Intl.DateTimeFormat('de-DE', { weekday: 'short' }).format(new Date(year, month - 1, day));
}

export function getDateKey(year: number, month: number, day: number): string {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
